import React, { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Users2, UserPlus, Trash2, Building2, Send, Loader2 } from "lucide-react";

export default function Collaborators({ propId, lang = "it" }) {
  const L = (it, en) => (lang === "en" ? en : it);
  const [members, setMembers] = useState([]);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("revenue_manager");
  const [busy, setBusy] = useState(false);
  const [showStruct, setShowStruct] = useState(false);
  const [sform, setSform] = useState({ structure_name: "", structure_type: "", address: "", rooms: "", booking_url: "", pms: "", message: "" });
  const [ssending, setSsending] = useState(false);

  const load = useCallback(async () => {
    if (!propId) return;
    try { const { data } = await api.get(`/properties/${propId}/members`); setMembers(data.members || []); }
    catch { setMembers([]); }
  }, [propId]);
  useEffect(() => { load(); }, [load]);

  const invite = async () => {
    if (!email) { toast.error(L("Inserisci un'email", "Enter an email")); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/properties/${propId}/members`, { email, role, origin: window.location.origin });
      toast.success(data.invited ? L("Invito inviato via email", "Invite sent by email") : L("Collaboratore collegato", "Collaborator linked"));
      setEmail(""); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Error"); }
    finally { setBusy(false); }
  };
  const remove = async (uid) => {
    if (!window.confirm(L("Rimuovere questo collaboratore da questa struttura?", "Remove this collaborator?"))) return;
    try { await api.delete(`/properties/${propId}/members/${uid}`); load(); } catch { toast.error("Error"); }
  };
  const requestStructure = async () => {
    if (!sform.structure_name) { toast.error(L("Nome struttura obbligatorio", "Structure name required")); return; }
    setSsending(true);
    try {
      await api.post(`/properties/request`, { ...sform, rooms: sform.rooms || 1 });
      toast.success(L("Richiesta inviata all'amministratore", "Request sent to the admin"));
      setShowStruct(false); setSform({ structure_name: "", structure_type: "", address: "", rooms: "", booking_url: "", pms: "", message: "" });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Error"); }
    finally { setSsending(false); }
  };
  const roleLabel = (r) => (r === "staff" ? L("Staff", "Staff") : L("Revenue Manager", "Revenue Manager"));
  const inCls = "w-full h-10 rounded-md border border-border bg-background px-3 text-sm";

  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="bg-card border border-border rounded-lg p-6 space-y-5" data-testid="mp-collaborators">
      <div className="flex items-center gap-2"><Users2 className="w-5 h-5 text-primary" /><h2 className="text-lg font-semibold">{L("Collaboratori della struttura", "Structure collaborators")}</h2></div>
      <p className="text-sm text-muted-foreground">{L("Invita un Revenue Manager o uno Staff. Se ha già un account (magari gestisce altre strutture) verrà semplicemente collegato; altrimenti riceverà un'email per impostare la password.", "Invite a Revenue Manager or Staff. If they already have an account they'll just be linked; otherwise they get an email to set their password.")}</p>

      <div className="flex flex-col sm:flex-row gap-2">
        <Input data-testid="collab-email" type="email" placeholder="email@collaboratore.it" value={email} onChange={(e) => setEmail(e.target.value)} className="flex-1" />
        <select data-testid="collab-role" value={role} onChange={(e) => setRole(e.target.value)} className={`${inCls} sm:w-52`}>
          <option value="revenue_manager">{L("Revenue Manager", "Revenue Manager")}</option>
          <option value="staff">{L("Staff (consultazione)", "Staff (read-only)")}</option>
        </select>
        <Button data-testid="collab-invite" onClick={invite} disabled={busy}>{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4 mr-1.5" />}{L("Invita", "Invite")}</Button>
      </div>

      {members.length > 0 && (
        <div className="space-y-2" data-testid="collab-list">
          {members.map((m) => (
            <div key={m.user_id} className="flex items-center justify-between border border-border rounded-md px-3 py-2.5" data-testid={`collab-item-${m.user_id}`}>
              <div><span className="text-sm font-medium">{m.email}</span><span className="ml-2 text-[11px] px-2 py-0.5 rounded-full bg-secondary">{roleLabel(m.role)}</span></div>
              <button onClick={() => remove(m.user_id)} className="p-1.5 rounded hover:bg-muted text-red-500" data-testid={`collab-remove-${m.user_id}`}><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
      )}

      <div className="pt-4 border-t border-border">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2"><Building2 className="w-5 h-5 text-primary" /><h3 className="font-semibold">{L("Aggiungi una nuova struttura", "Add a new structure")}</h3></div>
          <Button size="sm" variant="outline" onClick={() => setShowStruct(!showStruct)} data-testid="new-structure-toggle">{showStruct ? L("Chiudi", "Close") : L("Richiedi nuova struttura", "Request new structure")}</Button>
        </div>
        <p className="text-sm text-muted-foreground mt-1">{L("Ogni nuova struttura richiede l'approvazione dell'amministratore.", "Each new structure needs admin approval.")}</p>
        {showStruct && (
          <div className="mt-4 space-y-3" data-testid="new-structure-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <input className={inCls} placeholder={L("Nome struttura *", "Structure name *")} value={sform.structure_name} onChange={(e) => setSform({ ...sform, structure_name: e.target.value })} data-testid="ns-name" />
              <input className={inCls} placeholder={L("Tipologia", "Type")} value={sform.structure_type} onChange={(e) => setSform({ ...sform, structure_type: e.target.value })} data-testid="ns-type" />
              <input className={inCls} placeholder={L("Indirizzo", "Address")} value={sform.address} onChange={(e) => setSform({ ...sform, address: e.target.value })} data-testid="ns-address" />
              <input className={inCls} type="number" placeholder={L("Camere", "Rooms")} value={sform.rooms} onChange={(e) => setSform({ ...sform, rooms: e.target.value })} data-testid="ns-rooms" />
              <input className={inCls} placeholder={L("Link Booking", "Booking link")} value={sform.booking_url} onChange={(e) => setSform({ ...sform, booking_url: e.target.value })} data-testid="ns-booking" />
              <input className={inCls} placeholder="PMS" value={sform.pms} onChange={(e) => setSform({ ...sform, pms: e.target.value })} data-testid="ns-pms" />
            </div>
            <Button size="sm" onClick={requestStructure} disabled={ssending} data-testid="ns-submit">{ssending ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Send className="w-4 h-4 mr-1.5" />}{L("Invia richiesta", "Send request")}</Button>
          </div>
        )}
      </div>
    </motion.div>
  );
}
